import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DateRange } from "@/hooks/useDateRange";
import { analyticsRevenueService } from "@/services/analytics-revenue-service";
import { useAnalyticsOverviewRange } from "./useAnalyticsOverviewRange";
import {
  getPreviousAnalyticsDateRange,
  toAnalyticsDateParam,
} from "../utils/analyticsDateUtils";

export const analyticsRevenueKeys = {
  all: ["analytics-revenue"] as const,
  total: (hotelId: string, from: string, to: string) =>
    [...analyticsRevenueKeys.all, "total", hotelId, from, to] as const,
  avgCheck: (hotelId: string, from: string, to: string) =>
    [...analyticsRevenueKeys.all, "avg-check", hotelId, from, to] as const,
  byService: (hotelId: string, from: string, to: string) =>
    [...analyticsRevenueKeys.all, "by-service", hotelId, from, to] as const,
  distribution: (hotelId: string, from: string, to: string) =>
    [...analyticsRevenueKeys.all, "distribution", hotelId, from, to] as const,
};

export function useRevenueAnalytics(dateRange: DateRange) {
  const rangeParams = useAnalyticsOverviewRange(dateRange);
  const { hotelId, from, to, isReady } = rangeParams;
  const previousRange = useMemo(
    () => getPreviousAnalyticsDateRange(dateRange),
    [dateRange],
  );
  const previousFrom = toAnalyticsDateParam(previousRange?.from);
  const previousTo = toAnalyticsDateParam(previousRange?.to);
  const hasPreviousRange = Boolean(previousFrom && previousTo);

  const total = useQuery({
    queryKey: analyticsRevenueKeys.total(hotelId, from, to),
    queryFn: () => analyticsRevenueService.getTotal({ hotelId, from, to }),
    enabled: isReady,
  });

  const avgCheck = useQuery({
    queryKey: analyticsRevenueKeys.avgCheck(hotelId, from, to),
    queryFn: () => analyticsRevenueService.getAvgCheck({ hotelId, from, to }),
    enabled: isReady,
  });

  const previousTotal = useQuery({
    queryKey: analyticsRevenueKeys.total(
      hotelId,
      previousFrom ?? "",
      previousTo ?? "",
    ),
    queryFn: () =>
      analyticsRevenueService.getTotal({
        hotelId,
        from: previousFrom!,
        to: previousTo!,
      }),
    enabled: isReady && hasPreviousRange,
  });

  const previousAvgCheck = useQuery({
    queryKey: analyticsRevenueKeys.avgCheck(
      hotelId,
      previousFrom ?? "",
      previousTo ?? "",
    ),
    queryFn: () =>
      analyticsRevenueService.getAvgCheck({
        hotelId,
        from: previousFrom!,
        to: previousTo!,
      }),
    enabled: isReady && hasPreviousRange,
  });

  // Future: Revenue by channel / Trend by channel — re-enable GET /analytics/revenue/by-service.
  // const byService = useQuery({
  //   queryKey: analyticsRevenueKeys.byService(hotelId, from, to),
  //   queryFn: () => analyticsRevenueService.getByService({ hotelId, from, to }),
  //   enabled: isReady,
  // });

  const distribution = useQuery({
    queryKey: analyticsRevenueKeys.distribution(hotelId, from, to),
    queryFn: () =>
      analyticsRevenueService.getDistribution({ hotelId, from, to }),
    enabled: isReady,
  });

  return {
    ...rangeParams,
    previousRange,
    total,
    avgCheck,
    previousTotal,
    previousAvgCheck,
    distribution,
    isLoading: total.isLoading || avgCheck.isLoading || distribution.isLoading,
    isFetching:
      total.isFetching || avgCheck.isFetching || distribution.isFetching,
    isError: total.isError || avgCheck.isError || distribution.isError,
    error: total.error ?? avgCheck.error ?? distribution.error,
    isComparisonLoading:
      hasPreviousRange &&
      (previousTotal.isLoading || previousAvgCheck.isLoading),
    isComparisonFetching:
      hasPreviousRange &&
      (previousTotal.isFetching || previousAvgCheck.isFetching),
    isComparisonError: previousTotal.isError || previousAvgCheck.isError,
    comparisonError: previousTotal.error ?? previousAvgCheck.error,
  };
}
